import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router'
import { RootState } from '../redux/store'
import { useMemo } from 'react'
import { SchoolAccountState } from '../firebase/types-school'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import { TabNames } from './type'
import { selectUserAuth } from '../redux/userSlice'
import { selectSchoolUid, selectSchoolUserData } from '../redux/schoolSlice'
import { logger } from '../helpers/logger'
import { selectTeacherAccountState } from '../redux/teacherSlice'

interface IRoute {
    children: JSX.Element
    customizedFallbackRoute?: string
}

const appPath = 'dashboard' as const

const teacherPaths = {
    index: `/${appPath}/teacher`,
    MY_ACCOUNT: 'my-account',
    PREEMP: 'preemp',
    SETTINGS: {
        index: 'settings',
        DETAILS: 'details',
        NOTIFICATIONS: 'notifications',
    },
    HELP: 'help',
    REVIEW: 'review',
} as const

const schoolPaths = {
    index: `/${appPath}/school`,
    bookings: {
        index: 'bookings',
        upcoming: 'upcoming',
        all: 'all',
        date: {
            index: 'date',
            overview: 'overview',
            bookable: 'bookable',
        },
    },
    mySchool: {
        index: 'my-school',
        overview: 'overview',
        team: 'team',
        invoices: 'invoices',
        policies: 'policies',
    },
    settings: {
        index: 'settings',
    },
    help: 'help',
} as const

const centrePaths = {
    index: `/${appPath}/centre`,
    myCentre: {
        index: 'my-centre',
    },
    settings: {
        index: 'settings',
    },
    help: 'help',
} as const

export const tabNames = {
    landing: {
        teachers: 'teachers',
        schools: 'schools',
        eccCentre: 'ecc-centre',
        eccEducator: 'ecc-educator',
        about: 'about',
        helpDesk: 'help-desk',
        userBenefits: 'spark-user-benefits',
        termsOfService: 'terms-of-service',
        blogs: 'blog',
        guides: 'guides',
        pdResources: 'pdresources',

        teacherFAQ: 'teacher-frequently-asked-questions',
        schoolFAQ: 'school-frequently-asked-questions',

        shop: 'shop',
        checkoutSuccess: 'checkout-success',
    },
    auth: {
        login: 'login',
        register: 'register',
        verify: 'verify',
    },

    // App paths
    app: {
        index: appPath,
        teacher: teacherPaths,
        school: schoolPaths,
        centre: centrePaths,
    },

    // DOE Check
    employmentReview: 'employment-review',
    refereeSurvey: 'referee',
    docs: 'docs',
    payslips: 'payslips',
} as const

export const getPostAuthRedirectPath = (state: any) => {
    const payslipReq = state?.payslipReq

    if (payslipReq) {
        return `/${TabNames.PAYSLIP_API}/${payslipReq}`
    }

    return `/${TabNames.DASHBOARD}`
}

export function AuthRoute() {
    const auth = useSelector(selectUserAuth)
    const location = useLocation()

    if (auth.loading)
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="authRoute"
            />
        )

    return auth.status !== null ? (
        <Navigate to={getPostAuthRedirectPath(location.state)} replace />
    ) : (
        <Outlet />
    )
}

export const ProtectedRoute = () => {
    const auth = useSelector(selectUserAuth)

    if (auth.loading)
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="protectedRoute"
            />
        )

    if (!auth.status) {
        return <Navigate to={'/'} replace />
    } else {
        // NOTE: Email verification is temporarily disabled.
        return <Outlet />
    }
}

export const VerifyWrapper = () => {
    const auth = useSelector(selectUserAuth)

    logger.debug('render verify wrapper')

    if (auth.loading)
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="verifyWrapper"
            />
        )
    if (!auth.status) {
        return <Navigate to={`/${TabNames.LOGIN}`} replace />
    }

    // NOTE: Email verification is temporarily disabled.
    return <Navigate to={`/${TabNames.DASHBOARD}`} replace />
}

export const SchoolPermissionRoute = ({
    children,
    customizedFallbackRoute,
}: IRoute) => {
    const schoolId = useSelector(selectSchoolUid)
    const school = useSelector((state: RootState) => state.school.school)
    const schoolUser = useSelector(selectSchoolUserData)
    const schoolUserLoading = useSelector(
        (state: RootState) => state.school.schoolUser.loading
    )

    const canAccessMySchool = useMemo(() => {
        if (
            schoolUserLoading ||
            !schoolId ||
            !school?.account?.state ||
            !schoolUser?.school?.accountState ||
            !schoolUser.school?.authority
        )
            return undefined
        switch (school?.account?.state as SchoolAccountState) {
            case 'approved':
            case 'pendingApproval': {
                if (
                    schoolUser.school?.accountState === 'approved' ||
                    schoolUser.school?.authority === 'admin'
                ) {
                    return true
                }
                return false
            }
            case 'removed': {
                return false
            }
            default:
                return false
        }
    }, [
        schoolId,
        schoolUserLoading,
        school?.account?.state,
        schoolUser?.school?.accountState,
        schoolUser?.school?.authority,
    ])
    return canAccessMySchool === undefined ? (
        <LoadingSpinner />
    ) : canAccessMySchool ? (
        children
    ) : (
        <Navigate to={`${customizedFallbackRoute ?? '/'}`} replace />
    )
}

export const PreempDocRoute = ({ children }: IRoute) => {
    const accountState = useSelector(selectTeacherAccountState)

    if (accountState.status !== 'success') return <LoadingSpinner />

    if (
        accountState.data === 'incomplete' ||
        accountState.data === 'submitted' ||
        accountState.data === 'rejected'
    ) {
        return children
    } else {
        return <Navigate to={`/${TabNames.DASHBOARD}`} />
    }
}
